import { useState } from 'react';
import { useGetOriginalPlanQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useToast } from '@frameio/vapor';
import { SubscriptionUpdateSource } from '../../feature-gate/config';
import { usePlanManagementModalContext } from '../../modal/hooks';
import TEXT from './generated/TEXT/useResumePlan';
import { ResumePlanDialog } from './ResumePlanDialog';

const TOAST_DURATION = 4000;

TEXT.DEFINE({
  /** Error message when subscription info cannot be retrieved */
  error: 'Something went wrong retrieving your subscription info',
});

export const useResumePlan = ({ skip }: { skip?: boolean } = {}) => {
  const errorMessage = TEXT.error;

  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const { setSelectedPlanWithFeature } = usePlanManagementModalContext() ?? {};

  const accountId = useActiveAccountId();
  const { addToast } = useToast();

  const { data } = useGetOriginalPlanQuery({
    fetchPolicy: 'cache-and-network',
    variables: { accountId: accountId ?? '' },
    skip: skip || !accountId,
  });

  const showResumePlanDialog = () => setIsShowingConfirmation(true);
  const hideResumePlanDialog = () => setIsShowingConfirmation(false);

  const openOriginalPlanInPlanManagementModal = () => {
    const { originalPlan } = data?.account?.subscription ?? {};
    const subscriptionUpdateSource = SubscriptionUpdateSource.PAUSE_OFFER;

    if (!originalPlan || !setSelectedPlanWithFeature) {
      addToast(
        { title: errorMessage, variant: 'error' },
        { duration: TOAST_DURATION }
      );

      return;
    }

    setSelectedPlanWithFeature({ ...originalPlan, subscriptionUpdateSource });
  };

  const resumePlanDialog = (
    <ResumePlanDialog
      isShowing={isShowingConfirmation}
      onConfirm={openOriginalPlanInPlanManagementModal}
      onRequestClose={hideResumePlanDialog}
    />
  );

  return { resumePlanDialog, showResumePlanDialog };
};
